import React, { useState } from 'react';
import MediaDialog from '../components/MediaDialog';
import MediaViewport from '../components/MediaViewport';
import { Link } from 'react-router-dom';
import {
  UserFocus,
  UsersThree,
  ChartPieSlice,
  Package,
  DeviceMobile,
  TreeStructure,
} from '@phosphor-icons/react';
import SEO from '../components/SEO';
import ImageSlot from '../components/ImageSlot';
import styles from '../styles/pages/Pharma.module.css';

const architectureDiagrams = [
  {
    id: 'cdw-process',
    title: 'Commercial Data Warehouse Architecture',
    img: '/images/pharma/cdw-process-architecture.jpg',
    desc: 'A reference architecture connecting vendor data feeds to governed dashboards and, where needed, CRM updates.',
    bullets: [
      'Ingests claims, specialty pharmacy feeds, distribution drops, and digital telemetry into a unified warehouse.',
      'Automated Master Data Management (MDM) with human-in-the-loop stewardship for edge cases.',
      'Freshness and delivery checks before field reporting is published.',
      'Powers Executive, Field Force, Hub Operations, and Trade & Distribution reporting surfaces.',
    ],
  },
  {
    id: 'omnichannel-plan',
    title: '4-Level Omnichannel Framework',
    img: '/images/pharma/omnichannel-hierarchy-framework.jpg',
    desc: 'A unified framework connecting executive brand strategy down to tactical campaign execution across HCP, patient, and field channels.',
    bullets: [
      'Four distinct structural tiers bridging leadership oversight to tactical ad execution.',
      'Ways to compare physician-level digital engagement with specialty prescription volume.',
      'An approach to comparing promotional spend with available revenue measures.',
      'Automated field force trigger integration for high-priority prescribers.',
    ],
  },
  {
    id: 'specialty-distributor',
    title: 'Specialty Distribution & Trade Pipeline',
    img: '/images/pharma/distributor-edi-pipeline.jpg',
    desc: 'How inventory (EDI 852) and dispense/sales (EDI 867) files from national distributors move from SFTP drops into mastered reporting tables.',
    bullets: [
      'Automated SFTP ingestion across national and specialty distribution partners.',
      'Canonical distributor location mastering resolving parent and child accounts.',
      'Calculation of Days-on-Hand (DOH), potential stockout risks, and ex-factory demand.',
      'Direct integration with corporate finance and trade inventory dashboards.',
    ],
  },
  {
    id: 'dw-transition',
    title: 'Commercial Delivery Orchestration',
    img: '/images/pharma/delivery-sla-orchestration.jpg',
    desc: 'A reliable operational framework designed for launch execution and seamless long-term operational continuity.',
    bullets: [
      'Overnight staging and aggregation workflows for commercial records.',
      'Freshness and validation gates halting execution if data integrity risks emerge.',
      'Automated daily syndication to CRM, analytics warehouses, and partner portals.',
      'Monitored delivery schedules and pipeline freshness checks.',
    ],
  },
];

const capabilities = [
  {
    icon: UserFocus,
    num: '01',
    title: 'Digital Engagement by Prescriber',
    desc: 'Connect available digital media, medical portal, and unbranded disease education data to verified prescriber NPIs.',
    notes: [
      'Identifies gaps between digital media spend and prescriber-level engagement.',
      'Maps observed interactions to your commercial customer master.',
    ],
  },
  {
    icon: UsersThree,
    num: '02',
    title: 'Patient and Consumer Engagement',
    desc: 'Track consumer campaigns, educational portal engagement, and patient actions with privacy requirements in mind.',
    notes: [
      'Review media agency reporting alongside patient actions such as downloads and locator use.',
      'Captures patient toolkit downloads, doctor discussion guides, and locator usage.',
    ],
  },
  {
    icon: ChartPieSlice,
    num: '03',
    title: 'Media Delivery and Attribution',
    desc: 'Compare planned agency media budgets with platform delivery data to assess cross-channel performance and cost per qualified touch.',
    notes: [
      'Surfaces discrepancies between planned media schedules and delivered API actuals.',
      'Calculates CPM, CPC, and reported conversion rates across creative lines.',
    ],
  },
  {
    icon: Package,
    num: '04',
    title: 'Specialty Demand & Dispense Tracking',
    desc: 'Unify specialty pharmacy dispenses, wholesale inventory positions (EDI 852), and chargeback records (EDI 867) into a single demand view.',
    notes: [
      'Helps reconcile wholesale shipments with specialty pharmacy dispenses.',
      'Integrates copay assistance dynamics and payer reimbursement economics.',
    ],
  },
  {
    icon: DeviceMobile,
    num: '05',
    title: 'Field CRM & Rep Activity',
    desc: 'Bring rep calls, rep-triggered emails, speaker programs, and medical inquiries into a shared, territory-aligned activity view.',
    notes: [
      'Aligns multi-brand territory, district, and region hierarchies.',
      'Closes the loop between non-personal promotion and field rep interactions.',
    ],
  },
  {
    icon: TreeStructure,
    num: '06',
    title: 'Unified Customer Records (MDM)',
    desc: 'A master customer record that brings together corporate MDM, sales rosters, and available commercial signals.',
    notes: [
      'Identifies NPI duplication and unmapped account hierarchies across vendor drops.',
      'Supports consistent prescriber identification across analytics and field tools.',
    ],
  },
];

const Pharma = () => {
  const [activeModalDiagram, setActiveModalDiagram] = useState(null);

  return (
    <div className={`page ${styles.pharmaPage}`}>
      <SEO
        title="Commercial Life Sciences Data Foundations"
        description="Databricks commercial data architecture for life sciences: HCP/HCO mastering, distribution feeds, traceable reporting, and operational handover."
        path="/pharma"
      />

      {/* ─── STRATEGIC ADVISORY HERO ─── */}
      <header className="page-head page-head--media" id="top">
        <div className="page-head__text">
          <h1 className="kinetic-title">Connect the commercial picture.</h1>
          <p className="page-head__lede">
            Bring distribution, patient, media, and field data into one governed foundation.
            Our 90-day engagement defines the sources, reconciles commercial entities, and establishes measurable delivery controls.
          </p>
          <p className="page-path">The challenge <span aria-hidden="true">→</span> The connections <span aria-hidden="true">→</span> The blueprint</p>
        </div>
        <ImageSlot
          src="/images/aurablox-velocity.jpg"
          alt="AURABLOX architectural geometry connecting commercial data systems"
          ratio="4/3"
          label="Commercial data foundation — constructivist architecture"
        />
      </header>

      <main>
        <section className={styles.filmSection} id="pharma-film" aria-labelledby="pharma-film-heading">
          <div className={styles.filmInner}>
            <div className={styles.filmIntro}>
              <p className={styles.filmEyebrow}>AURABLOX / Specialty Pharma</p>
              <h2 id="pharma-film-heading">Connecting fragmented commercial feeds</h2>
              <p>
                Specialty pharma runs on distribution, pharmacy, media, and field data that rarely arrives
                as one coherent system. See how a governed commercial foundation connects the pieces—and
                where AURABLOX fits in.
              </p>
              <span className={styles.filmRuntime}>Featured film / 05:18</span>
            </div>
            <div className={styles.filmMedia}>
              <video
                controls
                playsInline
                preload="metadata"
                poster="/videos/architecting-the-governed-commercial-foundation-poster.jpg"
                aria-label="Architecting the Governed Commercial Foundation: specialty pharma challenges and AURABLOX's approach"
              >
                <source src="/videos/architecting-the-governed-commercial-foundation.mp4" type="video/mp4" />
                <a href="/videos/architecting-the-governed-commercial-foundation.mp4" download>Download the film</a>
              </video>
            </div>
          </div>
        </section>


        <section className={styles.founderSection} aria-labelledby="pharma-founder-heading">
          <div className={styles.founderInner}>
            <div className={styles.founderPortrait}>
              <img src="/images/josh-johnson.jpg" alt="Josh Johnson, AURABLOX Co-Founder and CEO" loading="lazy" />
            </div>
            <div className={styles.founderStory}>
              <h2 id="pharma-founder-heading">Commercial pharma data experience</h2>
              <p>
                Josh Johnson, AURABLOX Co-Founder and CEO, brings more than 15 years of data and systems experience
                across commercial pharma and distribution. At Beghou Consulting, he helped pharma teams turn complex
                feeds into cloud warehouses, reporting pipelines, and launch-ready commercial systems.
              </p>
              <p>
                Over the course of his career, Josh has supported four pharmaceutical product launches. That experience shapes our approach:
                understand the source data, design the contracts, and build systems teams can run.
              </p>
              <div className={styles.founderActions}>
                <a href="https://resume-jj.web.app/" target="_blank" rel="noopener noreferrer" className={styles.founderLink}>
                  Josh's background ↗
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* ─── THE CONNECTIVE TISSUE / GAP AUDITING ─── */}
        <section className="section" id="capabilities" aria-labelledby="capabilities-heading">
          <div className="section__header">
            <h2 id="capabilities-heading">What we connect</h2>
            <p>
              Six commercial domains, one governed data foundation. Explore the connections most relevant to your team.
            </p>
          </div>

          <div className={styles.iconGrid}>
            {capabilities.map(({ icon: Icon, num, title, desc, notes }) => (
              <article key={title} className={styles.iconCard}>
                <div className={styles.iconHeader}>
                  <div className={styles.iconWrapper}>
                    <Icon size={28} weight="regular" />
                  </div>
                  <span className={styles.iconNumber}>{num}</span>
                </div>
                <h3 className={styles.iconCardTitle}>{title}</h3>
                <p className={styles.iconCardDesc}>{desc}</p>
                <details className={styles.iconCardDetails}>
                  <summary>What we inspect <span aria-hidden="true">+</span></summary>
                  <ul className={styles.iconCardNotes}>
                    {notes.map((note) => <li key={note}>{note}</li>)}
                  </ul>
                </details>
              </article>
            ))}
          </div>
        </section>

        {/* ─── ARCHITECTURAL BLUEPRINTS ─── */}
        <section className="section" id="architectures" aria-labelledby="architecture-heading">
          <div className="section__header">
            <h2 id="architecture-heading">Commercial data references</h2>
            <p>
              Explore four reference architectures for connecting commercial data. Open a blueprint for the technical detail.
            </p>
          </div>

          <details className="reference-library">
            <summary>Explore four reference blueprints</summary>
          <div className={styles.diagramGrid}>
            {architectureDiagrams.map((diag) => (
              <article key={diag.id} className={styles.diagramCard}>
                <div
                  className={styles.diagramBanner}
                  onClick={() => setActiveModalDiagram(diag)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      setActiveModalDiagram(diag);
                    }
                  }}
                  aria-label={`Enlarge ${diag.title}`}
                >
                  <img src={diag.img} alt={diag.title} loading="lazy" />
                  <span className={styles.expandBadge}>Enlarge blueprint +</span>
                </div>
                <div className={styles.diagramBody}>
                  <h3 className={styles.diagramTitle}>{diag.title}</h3>
                  <p className={styles.diagramDesc}>{diag.desc}</p>
                  <div className={styles.diagramFooter}>
                    <button
                      className={styles.diagramBtn}
                      onClick={() => setActiveModalDiagram(diag)}
                    >
                      Enlarge blueprint +
                    </button>
                  </div>
                </div>
              </article>
            ))}
          </div>
          </details>
        </section>

        {/* ─── HOW WE ENGAGE (THE 3 STRATEGIC PATHWAYS) ─── */}
        <section className="section" id="engage" aria-labelledby="engage-heading">
          <div className="section__header"><h2 id="engage-heading">The commercial data foundation engagement</h2><p>A focused 90-day plan for agreed commercial feeds and master data, with a blueprint, lakehouse build, reconciliation controls, and operational handover.</p></div>
          <div className="offer-assurance"><p>Start with the commercial deadline, source scope, platform readiness, and the sponsor who can approve access and schema decisions.</p><Link to="/solutions" className="btn">Review the four delivery stages ↗</Link></div>
        </section>
      </main>

      {/* ─── LIGHTBOX MODAL ─── */}
      {activeModalDiagram && (
        <MediaDialog
          className={styles.modalBackdrop}
          title={activeModalDiagram.title}
          onClose={() => setActiveModalDiagram(null)}
        >
          <div
            className={styles.modalContent}
            onClick={(e) => e.stopPropagation()}
          >
            <div className={styles.modalHeader}>
              <h3 className={styles.modalTitle}>{activeModalDiagram.title}</h3>
              <button
                className={styles.modalCloseBtn}
                onClick={() => setActiveModalDiagram(null)}
              >
                Close (ESC) ✕
              </button>
            </div>
            <MediaViewport
              src={activeModalDiagram.img}
              alt={activeModalDiagram.title}
              description={activeModalDiagram.desc}
              bullets={activeModalDiagram.bullets}
            />
          </div>
        </MediaDialog>
      )}
    </div>
  );
};

export default Pharma;
